import React, { useId, useState } from 'react';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';
import './LeadCaptureForm.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Two-field lead capture for pages that have no other way to convert a reader.
 *
 * Most of the site's search traffic lands on blog posts and city pages, which
 * previously offered only navigation links — a reader who was ready to buy or
 * sell had to find their own way to a vehicle page. `source` records which page
 * produced the lead so we can tell which content is actually worth its traffic.
 */
const LeadCaptureForm = ({
  source,
  requestType = 'general',
  heading = 'Looking to buy or sell a taxi?',
  subheading = 'Leave your number and our team will call you back.',
  buttonText = 'Request a Callback',
  messagePlaceholder = 'What are you looking for? (optional)',
  defaultMessage = '',
  compact = false,
  // Optional qualifier shown above the name field, as { label, options: [] }.
  // Its value is prepended to the message so the person who calls back knows
  // what the enquiry is about before dialling. Off by default — every existing
  // caller renders exactly as before.
  selectField = null,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  // `null` means "the reader has not touched this", so a page that keeps
  // `defaultMessage` in sync with its own state (the valuation calculator) can
  // keep updating it — until the reader types, at which point their text wins.
  const [message, setMessage] = useState(null);
  const effectiveMessage = message === null ? defaultMessage : message;
  const [selected, setSelected] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — humans never see this
  const [status, setStatus] = useState('idle'); // idle | sending | done
  const [error, setError] = useState('');
  // The title is a <p>, so the form takes its accessible name from it
  // explicitly rather than from a heading. Declared before the early return.
  const headingId = useId();

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const digits = phone.replace(/\D/g, '').slice(-10);
    if (name.trim().length < 2) return setError('Please enter your name');
    if (!/^[6-9]\d{9}$/.test(digits)) return setError('Please enter a valid 10-digit mobile number');

    setStatus('sending');
    try {
      await axios.post(`${API_URL}/buyer/callback-request`, {
        name: name.trim(),
        phone: digits,
        request_type: requestType,
        message: [selected ? `[${selected}]` : '', effectiveMessage.trim()].filter(Boolean).join(' ') || null,
        source,
        source_url: typeof window !== 'undefined' ? window.location.pathname : null,
        website,
      });
      setStatus('done');
      trackEvent('lead_submit', { source, request_type: requestType, qualifier: selected || undefined });
    } catch (err) {
      setStatus('idle');
      setError(err?.response?.data?.error || 'Could not send that — please try again or call us.');
    }
  };

  if (status === 'done') {
    return (
      <div className={`leadcap leadcap-done${compact ? ' leadcap-compact' : ''}`} data-testid="leadcap-success">
        <h3>Thanks{name.trim() ? `, ${name.trim().split(' ')[0]}` : ''}! We'll call you shortly.</h3>
        <p>Our team usually calls back within an hour during business hours. You can also reach us on <a href="tel:+919560759834">+91 95607 59834</a>.</p>
      </div>
    );
  }

  return (
    <form className={`leadcap${compact ? ' leadcap-compact' : ''}`} onSubmit={submit} data-testid="leadcap-form" aria-labelledby={headingId}>
      {/* A styled <p>, not an <h3>. A form title is a call to action, not a
          section of the page — as a heading it put "Not sure which permit your
          vehicle needs?" and every other CTA into the outline that crawlers and
          AI engines use to match passages to queries. LeadCaptureForm.css keeps
          it looking exactly as the h3 did. */}
      <p id={headingId} className="leadcap-heading">{heading}</p>
      {subheading && <p className="leadcap-sub">{subheading}</p>}

      {selectField && (
        <select
          className="leadcap-input leadcap-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="leadcap-select"
          aria-label={selectField.label}
        >
          <option value="">{selectField.label}</option>
          {selectField.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}

      <div className="leadcap-fields">
        <input
          type="text"
          className="leadcap-input"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          required
          data-testid="leadcap-name"
        />
        <input
          type="tel"
          className="leadcap-input"
          placeholder="Mobile number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          inputMode="numeric"
          required
          data-testid="leadcap-phone"
        />
      </div>

      {!compact && (
        <textarea
          className="leadcap-input leadcap-textarea"
          placeholder={messagePlaceholder}
          value={effectiveMessage}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          data-testid="leadcap-message"
        />
      )}

      {/* Honeypot: hidden from people and screen readers, irresistible to bots */}
      <input
        type="text"
        name="website"
        className="leadcap-hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />

      {error && <p className="leadcap-error" role="alert">{error}</p>}

      <button type="submit" className="leadcap-btn" disabled={status === 'sending'} data-testid="leadcap-submit">
        {status === 'sending' ? 'Sending…' : buttonText}
      </button>
      <p className="leadcap-fineprint">No spam. We only call about your enquiry.</p>
    </form>
  );
};

export default LeadCaptureForm;
